import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, Plus } from 'lucide-react';
import { InteractiveCard } from '@/components/ui/interactive-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import SectionCard from '@/components/dashboard/widgets/SectionCard';
import type { SimulationResponse } from '@/types/simulation';
import { formatDateShort } from '@/utils/formatDate';
import DocumentsStatusBadge from '@/components/simulation/DocumentsStatusBadge';

type Props = {
  simulations: SimulationResponse[];
  loading: boolean;
  /** Where the "+" action leads (admin vs edit). */
  createHref?: string;
};

export default function SimulationsCard({ simulations, loading, createHref = '/admin/simulations/new' }: Props) {
  const { t } = useTranslation();

  // Most recent first by updatedAt/createdAt
  const sorted = [...simulations].sort((a, b) => {
    const ta = new Date(a.updatedAt ?? a.createdAt).getTime();
    const tb = new Date(b.updatedAt ?? b.createdAt).getTime();
    return tb - ta;
  });

  return (
    <SectionCard
      title={t('dashboard.manager.simulations', 'Simulaciones')}
      action={
        <div className="flex items-center gap-1.5">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/simulations">
              {t('common.viewAll', 'Ver todas')}
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
          <Button asChild size="sm">
            <Link to={createHref}>
              <Plus className="size-4" />
              {t('dashboard.manager.newSim', 'Nueva')}
            </Link>
          </Button>
        </div>
      }
      contentClassName="space-y-2"
    >
      {loading ? (
        <>
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <ClipboardList className="size-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {t('dashboard.manager.noSims', 'No hay simulaciones aún')}
          </p>
          <Button asChild size="sm">
            <Link to={createHref}>
              <Plus className="size-4" />
              {t('dashboard.manager.createFirst', 'Crear la primera')}
            </Link>
          </Button>
        </div>
      ) : (
        sorted.slice(0, 5).map((sim) => (
          <InteractiveCard key={sim.id} to={`${createHref}?id=${sim.id}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">{sim.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge variant="secondary" className="font-normal">
                  {t(`simulationType.${sim.simulationType}`, sim.simulationType)}
                </Badge>
                <span>·</span>
                <span>{formatDateShort(sim.updatedAt ?? sim.createdAt)}</span>
                {sim.assignedSalespersonIds.length > 0 && (
                  <>
                    <span>·</span>
                    <span>
                      {sim.assignedSalespersonIds.length}{' '}
                      {t('dashboard.manager.assigned', 'asignados')}
                    </span>
                  </>
                )}
              </div>
              {sim.documentsReadiness !== 'NoDocuments' && (
                <div className="mt-1.5">
                  <DocumentsStatusBadge simulation={sim} role="manager" />
                </div>
              )}
            </div>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
          </InteractiveCard>
        ))
      )}
    </SectionCard>
  );
}
